import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSanteData } from "../hooks/useSanteData";
import NavRail from "../components/NavRail";

/* ============================================================
   SANTÉ SYSTÈME — Backoffice bot memecoin Solana (V1)
   Full-screen self-contained (dark terminal), même side-rail que le Cockpit.
   Câblé sur les vraies données (useSanteData) : streams Redis (depth + freshness),
   latence d'envoi, mode + kill-switches, débit. Dégradation par carte.
   Ressources hôte (CPU/RAM/Disque/Uptime/Load) + statut des 22 containers du bot
   via /api/system/resources (Prometheus : node-exporter + cAdvisor). n/d si indispo.
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
.ck{
  --bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;--text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;
}
.ck, .ck *{font-family:'JetBrains Mono',ui-monospace,monospace;box-sizing:border-box;margin:0;padding:0;}
.ck{background:var(--bg);color:var(--text);height:100vh;display:flex;overflow:hidden;font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}
.sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;display:flex;flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;margin-bottom:14px;box-shadow:0 0 14px rgba(255,59,92,.45);}
.sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:var(--text2);cursor:pointer;position:relative;transition:all .12s;font-size:16px;border:none;background:transparent;}
.sb-item:hover{background:var(--surface);color:var(--text);}
.sb-item.on{background:var(--surface);color:var(--green);box-shadow:inset 2px 0 0 var(--green);}
.sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.sb-item:hover .tip{opacity:1;}
.sb-spacer{flex:1;}
.main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.top{display:flex;align-items:center;gap:18px;padding:11px 18px;border-bottom:1px solid var(--border);background:#0c0d11;flex:0 0 auto;}
.mode{display:flex;align-items:center;gap:9px;padding:9px 16px;border-radius:9px;font-weight:800;font-size:14px;letter-spacing:.04em;}
.mode.live{background:var(--red);color:#fff;box-shadow:0 0 0 1px rgba(255,59,92,.6),0 0 22px rgba(255,59,92,.35);}
.mode.paper{background:rgba(0,224,138,.13);color:var(--green);border:1px solid rgba(0,224,138,.4);}
.dot{width:9px;height:9px;border-radius:50%;background:#fff;animation:pulse 1.3s infinite;}
@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(255,255,255,.7);}70%{box-shadow:0 0 0 7px rgba(255,255,255,0);}100%{box-shadow:0 0 0 0 rgba(255,255,255,0);}}
.mode.paper .dot{background:var(--green);animation:pulseg 2.4s infinite;}
@keyframes pulseg{0%{box-shadow:0 0 0 0 rgba(0,224,138,.5);}70%{box-shadow:0 0 0 6px rgba(0,224,138,0);}100%{box-shadow:0 0 0 0 rgba(0,224,138,0);}}
.top-spacer{flex:1;}.clock{font-size:11px;color:var(--text2);text-align:right;}.clock b{color:var(--text);font-weight:600;}
.panic{display:flex;align-items:center;gap:8px;background:var(--red);color:#fff;border:none;border-radius:9px;padding:11px 18px;font-weight:800;font-size:13px;letter-spacing:.03em;cursor:pointer;font-family:inherit;box-shadow:0 0 0 1px rgba(255,59,92,.7),0 6px 22px rgba(255,59,92,.4);transition:transform .1s;}
.panic:hover{transform:translateY(-1px);}
.page{flex:1;overflow:auto;padding:16px;}
.h1{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--text2);font-weight:600;margin-bottom:14px;display:flex;align-items:center;gap:10px;}
.ok-badge{font-size:11px;color:var(--green);background:rgba(0,224,138,.12);border:1px solid rgba(0,224,138,.35);padding:3px 10px;border-radius:20px;display:flex;align-items:center;gap:6px;letter-spacing:.04em;}
.ok-badge .d{width:7px;height:7px;border-radius:50%;background:var(--green);animation:pg 2s infinite;}
@keyframes pg{0%{box-shadow:0 0 0 0 rgba(0,224,138,.5);}70%{box-shadow:0 0 0 6px rgba(0,224,138,0);}100%{box-shadow:0 0 0 0 rgba(0,224,138,0);}}
.res{display:grid;grid-template-columns:repeat(6,1fr);gap:10px;margin-bottom:16px;}
@media(max-width:1100px){.res{grid-template-columns:repeat(3,1fr);}}
@media(max-width:640px){.res{grid-template-columns:repeat(2,1fr);}}
.rcard{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:11px 13px;}
.rcard .k{font-size:9.5px;color:var(--text2);letter-spacing:.07em;text-transform:uppercase;}
.rcard .v{font-size:19px;font-weight:800;margin:4px 0 7px;}
.rcard .v.nd{color:var(--text2);font-weight:600;}
.rbar{height:6px;background:#0a0b0e;border-radius:3px;overflow:hidden;border:1px solid var(--border);}
.rfill{height:100%;transition:width .5s,background .3s;}
.grid2{display:grid;grid-template-columns:1.2fr 1fr;gap:12px;margin-bottom:12px;}
@media(max-width:980px){.grid2{grid-template-columns:1fr;}}
.card{background:var(--surface);border:1px solid var(--border);border-radius:12px;padding:14px;}
.card-ti{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--text2);font-weight:600;margin-bottom:12px;display:flex;justify-content:space-between;align-items:center;}
.card-meta{font-size:10px;color:var(--text2);letter-spacing:0;text-transform:none;font-weight:400;}
.card-meta.err{color:var(--red);}
.note{font-size:11.5px;color:var(--text2);padding:14px 6px;text-align:center;border:1px dashed var(--border);border-radius:8px;}
.svc{display:flex;align-items:center;gap:11px;padding:9px 0;border-top:1px solid #16191f;}
.svc:first-of-type{border-top:none;}
.svc .pill{width:9px;height:9px;border-radius:50%;flex:0 0 9px;}
.pill.ok{background:var(--green);box-shadow:0 0 8px rgba(0,224,138,.6);}
.pill.warn{background:var(--amber);box-shadow:0 0 8px rgba(255,176,32,.6);}
.pill.down{background:var(--red);box-shadow:0 0 8px rgba(255,59,92,.6);animation:bl 1s infinite;}
.pill.absent{background:var(--text2);}
@keyframes bl{0%,100%{opacity:1;}50%{opacity:.3;}}
.svc .nm{flex:1;font-size:12.5px;font-weight:600;overflow:hidden;}
.svc .nm small{display:block;font-weight:400;color:var(--text2);font-size:10px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.svc .len{font-size:13px;color:var(--text);width:74px;text-align:right;font-weight:700;}
.svc .hb{font-size:11px;width:78px;text-align:right;}
.svc .st{font-size:9.5px;font-weight:700;width:64px;text-align:right;}
.st.ok{color:var(--green);}.st.warn{color:var(--amber);}.st.down{color:var(--red);}
.lat-lbl{display:flex;justify-content:space-between;font-size:10px;color:var(--text2);margin-bottom:4px;}
.gauge-big{display:flex;align-items:baseline;gap:8px;margin:6px 0 10px;}
.gauge-big .num{font-size:34px;font-weight:800;letter-spacing:-.02em;line-height:1;}
.gauge-big .unit{font-size:13px;color:var(--text2);font-weight:500;}
.s-bar{height:10px;border-radius:6px;background:#0a0b0e;border:1px solid var(--border);overflow:hidden;}
.s-bar-fill{height:100%;border-radius:5px;transition:width .5s ease,background .3s;}
.s-bar-legend{margin-top:8px;font-size:10px;color:var(--text2);display:flex;justify-content:space-between;}
.kill-list{display:flex;flex-direction:column;gap:9px;}
.kill-item{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:9px 11px;border-radius:8px;background:var(--surface2);}
.kill-name{font-size:11px;color:var(--text2);}
.kill-name b{color:var(--text);font-weight:600;}
.kill-reason{font-size:10px;color:var(--text2);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:150px;}
.k-pill{display:flex;align-items:center;gap:6px;font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;white-space:nowrap;}
.k-pill.off{background:rgba(138,143,152,.14);color:var(--text2);}
.k-pill.on{background:rgba(255,59,92,.16);color:var(--red);}
.k-pill.on .pd{width:7px;height:7px;border-radius:50%;background:var(--red);animation:pulsr 1s infinite;}
@keyframes pulsr{0%,100%{opacity:1;}50%{opacity:.3;}}
.k-pill.warm{background:rgba(255,176,32,.15);color:var(--amber);}
.ratio{font-size:15px;font-weight:800;}
.mode-badge{display:inline-flex;align-items:center;gap:8px;padding:8px 14px;border-radius:9px;font-weight:800;font-size:13px;letter-spacing:.04em;margin-bottom:12px;}
.mode-badge.live{background:var(--red);color:#fff;box-shadow:0 0 0 1px rgba(255,59,92,.6),0 0 18px rgba(255,59,92,.3);}
.mode-badge.paper{background:rgba(0,224,138,.13);color:var(--green);border:1px solid rgba(0,224,138,.4);}
.mode-badge .mdot{width:8px;height:8px;border-radius:50%;background:currentColor;}
.stats-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px;}
.stat{display:flex;flex-direction:column;gap:3px;padding:11px;background:var(--surface2);border-radius:7px;justify-content:center;}
.stat .k{font-size:10px;color:var(--text2);letter-spacing:.06em;text-transform:uppercase;}
.stat .v{font-size:24px;font-weight:800;letter-spacing:-.02em;}
.stat .v.muted{color:var(--text2);}
.rcard .sub{font-size:9.5px;color:var(--text2);margin-top:5px;}
.cgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(168px,1fr));gap:8px;}
.cbox{display:flex;align-items:center;gap:8px;padding:8px 10px;background:var(--surface2);border-radius:8px;border:1px solid transparent;}
.cbox.down{border-color:rgba(255,59,92,.5);animation:bl 1.1s infinite;}
.cbox .cp{width:8px;height:8px;border-radius:50%;flex:0 0 8px;}
.cbox .cn{flex:1;font-size:11px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.cbox .cm{font-size:9.5px;color:var(--text2);text-align:right;white-space:nowrap;line-height:1.25;}
`;

const STREAM_ORDER = [
  "ls:mints",
  "ls:activations",
  "tx:send_requests",
  "tx:send_results",
  "telegram:signals",
];

// Pastille fraîcheur : <30s vert, 30-120s ambre, >120s ou null rouge.
function ageColor(age) {
  if (age === null || age === undefined) return "var(--red)";
  if (age < 30) return "var(--green)";
  if (age <= 120) return "var(--amber)";
  return "var(--red)";
}
// État stream dérivé de la fraîcheur (pour la pastille + le label).
function streamState(present, age) {
  if (!present) return "absent";
  if (age === null || age === undefined) return "down";
  if (age < 30) return "ok";
  if (age <= 120) return "warn";
  return "down";
}
// Latence send : <50 vert, 50-150 ambre, >150 rouge.
function latColor(ms) {
  if (ms === null || ms === undefined) return "var(--text2)";
  if (ms < 50) return "var(--green)";
  if (ms <= 150) return "var(--amber)";
  return "var(--red)";
}

const fmtAge = (s) => {
  if (s === null || s === undefined) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m${String(Math.round(s % 60)).padStart(2, "0")}`;
};
const fmtNum = (v) =>
  v === null || v === undefined ? "—" : Number(v).toLocaleString("fr-FR");

// Couleur d'usage : <60% vert, 60-85% ambre, >85% rouge.
function usageColor(pct) {
  if (pct === null || pct === undefined) return "var(--text2)";
  if (pct < 60) return "var(--green)";
  if (pct <= 85) return "var(--amber)";
  return "var(--red)";
}
const fmtUptime = (s) => {
  if (s === null || s === undefined) return "n/d";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d}j ${h}h` : `${h}h ${Math.floor((s % 3600) / 60)}m`;
};
const pct1 = (v) => (v === null || v === undefined ? null : Math.round(v * 10) / 10);

export default function SantePage() {
  const navigate = useNavigate();
  const { health, instant, risk, resources } = useSanteData();
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // --- Streams (carte Services) -----------------------------------------
  const hData = health.data || {};
  const rawStreams = Array.isArray(hData.streams) ? hData.streams : [];
  const byName = {};
  rawStreams.forEach((s) => {
    if (s && s.name) byName[s.name] = s;
  });
  const orderedNames = [
    ...STREAM_ORDER,
    ...rawStreams
      .map((s) => s && s.name)
      .filter((n) => n && !STREAM_ORDER.includes(n)),
  ];
  const presentStates = orderedNames.map((name) => {
    const s = byName[name];
    return streamState(!!s, s ? s.last_age_s : undefined);
  });
  const okCount = presentStates.filter((st) => st === "ok").length;
  const totalCount = orderedNames.length;
  const allOk = !health.isError && totalCount > 0 && okCount === totalCount;

  // --- Latence (carte Latence) ------------------------------------------
  const latency =
    hData.send_latency_ms ??
    (instant.data ? instant.data.send_latency_ms : undefined) ??
    null;
  const latPct =
    latency === null || latency === undefined
      ? 0
      : Math.min(100, (latency / 200) * 100);

  // --- Mode + kill-switches (carte Mode) --------------------------------
  const r = risk.data || {};
  const mode = (r.mode || "paper").toUpperCase();
  const isLive = mode === "LIVE";
  const killBuys = r.kill_buys || {};
  const stopSells = r.stop_sells || {};
  const consec = r.consecutive_losses ?? 0;
  const sf1 = r.sf1_threshold ?? 3;

  // --- Débit (carte Débit) ----------------------------------------------
  const inst = instant.data || {};

  // --- Ressources hôte + containers (Prometheus) ------------------------
  const resData = resources.data || {};
  const rh = resData.host || {};
  const resErr = resources.isError || resData.available === false;
  const containers = Array.isArray(resData.containers) ? resData.containers : [];
  const cUp = resData.containers_up ?? containers.filter((c) => c.up).length;
  const cTotal = resData.containers_total ?? containers.length;
  const memPct = rh.mem ? pct1(rh.mem.percent) : null;
  const diskPct = rh.disk ? pct1(rh.disk.percent) : null;
  const load1 = Array.isArray(rh.load_avg) ? rh.load_avg[0] : null;
  const loadPct =
    load1 != null && rh.cpu_count
      ? Math.min(100, (load1 / rh.cpu_count) * 100)
      : null;
  const contPct = cTotal > 0 ? (cUp / cTotal) * 100 : null;
  // 6 cartes ressources, pilotées par les vraies métriques (n/d si indispo)
  const resCards = [
    { k: "Uptime", val: resErr ? "n/d" : fmtUptime(rh.uptime_s), pct: null, color: "var(--cyan)" },
    { k: "CPU", val: resErr || rh.cpu_percent == null ? "n/d" : `${pct1(rh.cpu_percent)}%`, pct: rh.cpu_percent, color: usageColor(rh.cpu_percent) },
    { k: "RAM", val: resErr || memPct == null ? "n/d" : `${memPct}%`, pct: memPct, color: usageColor(memPct), sub: rh.mem ? `${rh.mem.used_gb} / ${rh.mem.total_gb} GB` : null },
    { k: "Disque", val: resErr || diskPct == null ? "n/d" : `${diskPct}%`, pct: diskPct, color: usageColor(diskPct), sub: rh.disk ? `${rh.disk.used_gb} / ${rh.disk.total_gb} GB` : null },
    { k: `Load (${rh.cpu_count || "?"} cœurs)`, val: resErr || load1 == null ? "n/d" : load1.toFixed(2), pct: loadPct, color: usageColor(loadPct), sub: Array.isArray(rh.load_avg) ? `${rh.load_avg.map((l) => (l == null ? "—" : l.toFixed(2))).join(" · ")}` : null },
    { k: "Containers", val: resErr ? "n/d" : `${cUp} / ${cTotal}`, pct: contPct, color: cUp === cTotal && cTotal > 0 ? "var(--green)" : "var(--red)" },
  ];

  const t = clock.toLocaleTimeString("fr-FR", {
    hour12: false,
    timeZone: "Europe/Paris",
  });

  return (
    <div className="ck">
      <style>{CSS}</style>

      <NavRail />

      <div className="main">
        <div className="top">
          {isLive ? (
            <div className="mode live"><span className="dot" /> LIVE — CAPITAL RÉEL</div>
          ) : (
            <div className="mode paper"><span className="dot" /> PAPER</div>
          )}
          <div className="top-spacer" />
          <div className="clock"><b>{t}</b><br />Europe/Paris</div>
          <button
            className="panic"
            onClick={() => navigate("/cockpit")}
            title="Kill-switches — gérés depuis le Cockpit"
          >
            ⏻ STOP
          </button>
        </div>

        <div className="page">
          <div className="h1">
            Santé système
            {health.isError ? (
              <span
                className="ok-badge"
                style={{
                  color: "var(--red)",
                  background: "rgba(255,59,92,.12)",
                  borderColor: "rgba(255,59,92,.35)",
                }}
              >
                <span className="d" style={{ background: "var(--red)" }} /> /system/health
                indisponible
              </span>
            ) : allOk ? (
              <span className="ok-badge">
                <span className="d" /> TOUS STREAMS FRAIS
              </span>
            ) : (
              <span
                className="ok-badge"
                style={{
                  color: "var(--amber)",
                  background: "rgba(255,176,32,.12)",
                  borderColor: "rgba(255,176,32,.35)",
                }}
              >
                <span className="d" style={{ background: "var(--amber)" }} />{" "}
                {totalCount - okCount} STREAM{totalCount - okCount > 1 ? "S" : ""} DÉGRADÉ
                {totalCount - okCount > 1 ? "S" : ""}
              </span>
            )}
          </div>

          {/* ressources hôte — node-exporter via Prometheus */}
          <div className="res">
            {resCards.map((c) => (
              <div className="rcard" key={c.k}>
                <div className="k">{c.k}</div>
                <div className={`v ${c.val === "n/d" ? "nd" : ""}`} style={c.val === "n/d" ? undefined : { color: c.color }}>
                  {c.val}
                </div>
                <div className="rbar">
                  <div
                    className="rfill"
                    style={{
                      width: `${c.pct == null ? 0 : Math.max(0, Math.min(100, c.pct))}%`,
                      background: c.pct == null ? "var(--text2)" : c.color,
                    }}
                  />
                </div>
                {c.sub ? <div className="sub">{c.sub}</div> : null}
              </div>
            ))}
          </div>

          <div className="grid2">
            {/* Services = streams Redis (depth + freshness) */}
            <div className="card">
              <div className="card-ti">
                Streams Redis
                <span className={`card-meta ${health.isError ? "err" : ""}`}>
                  {health.isError
                    ? "indispo"
                    : health.isFetching
                    ? "…"
                    : `${okCount}/${totalCount} frais`}
                </span>
              </div>
              {health.isError ? (
                <div className="note">
                  /system/health indisponible — impossible de lire la profondeur des
                  streams.
                </div>
              ) : (
                orderedNames.map((name, i) => {
                  const s = byName[name];
                  const len = s ? s.len : undefined;
                  const age = s ? s.last_age_s : undefined;
                  const stt = presentStates[i];
                  const stLabel =
                    stt === "ok"
                      ? "OK"
                      : stt === "warn"
                      ? "DÉGRADÉ"
                      : stt === "absent"
                      ? "ABSENT"
                      : "HS";
                  const stCls =
                    stt === "ok" ? "ok" : stt === "warn" ? "warn" : "down";
                  return (
                    <div className="svc" key={name}>
                      <span className={`pill ${stt === "absent" ? "absent" : stCls}`} />
                      <span className="nm">
                        {name}
                        <small>depth + fraîcheur dernière entrée</small>
                      </span>
                      <span className="len">{s ? fmtNum(len) : "—"}</span>
                      <span className="hb" style={{ color: ageColor(age) }}>
                        ♥ {fmtAge(age)}
                      </span>
                      <span className={`st ${stCls}`}>{stLabel}</span>
                    </div>
                  );
                })
              )}
            </div>

            {/* Latence d'envoi */}
            <div className="card">
              <div className="card-ti">
                Latence d'envoi
                <span className="card-meta">{latency === null ? "—" : "send latency"}</span>
              </div>
              <div className="lat-lbl">
                <span>send latency</span>
                <span style={{ color: "var(--text)" }}>
                  {latency === null || latency === undefined
                    ? "—"
                    : `${Math.round(latency)} ms`}
                </span>
              </div>
              <div className="gauge-big">
                <span className="num" style={{ color: latColor(latency) }}>
                  {latency === null || latency === undefined
                    ? "—"
                    : Math.round(latency)}
                </span>
                <span className="unit">ms</span>
              </div>
              <div className="s-bar">
                <div
                  className="s-bar-fill"
                  style={{ width: `${latPct}%`, background: latColor(latency) }}
                />
              </div>
              <div className="s-bar-legend">
                <span>&lt;50 ok</span>
                <span>50-150</span>
                <span>&gt;150</span>
              </div>
              <div
                style={{
                  borderTop: "1px solid #16191f",
                  marginTop: 12,
                  paddingTop: 12,
                }}
              >
                <div className="lat-lbl">
                  <span>Slot Solana (node)</span>
                  <span>n/d</span>
                </div>
                <div className="gauge-big" style={{ margin: "6px 0 0" }}>
                  <span className="num" style={{ color: "var(--text2)", fontSize: 22 }}>
                    n/d
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid2">
            {/* Mode + kill-switches */}
            <div className="card">
              <div className="card-ti">
                Mode &amp; kill-switches
                <span className={`card-meta ${risk.isError ? "err" : ""}`}>
                  {risk.isError ? "indispo" : risk.isFetching ? "…" : "live"}
                </span>
              </div>
              <div className={`mode-badge ${isLive ? "live" : "paper"}`}>
                <span className="mdot" /> {isLive ? "LIVE — CAPITAL RÉEL" : "PAPER"}
              </div>
              <div className="kill-list">
                <div className="kill-item">
                  <span className="kill-name">
                    <b>kill:buys</b>
                    <br />achats live
                    {killBuys.armed && killBuys.reason ? (
                      <div className="kill-reason" title={killBuys.reason}>
                        {killBuys.reason}
                      </div>
                    ) : null}
                  </span>
                  {killBuys.armed ? (
                    <span className="k-pill on">
                      <span className="pd" /> ARMÉ
                    </span>
                  ) : (
                    <span className="k-pill off">off</span>
                  )}
                </div>
                <div className="kill-item">
                  <span className="kill-name">
                    <b>kill:sells</b>
                    <br />frein anti-storm
                    {stopSells.armed && stopSells.reason ? (
                      <div className="kill-reason" title={stopSells.reason}>
                        {stopSells.reason}
                      </div>
                    ) : null}
                  </span>
                  {stopSells.armed ? (
                    <span className="k-pill on">
                      <span className="pd" /> ARMÉ
                    </span>
                  ) : (
                    <span className="k-pill off">off</span>
                  )}
                </div>
                <div className="kill-item">
                  <span className="kill-name">
                    <b>SF-1</b>
                    <br />pertes consécutives
                  </span>
                  <span
                    className={`k-pill ${
                      consec >= sf1 ? "on" : consec > 0 ? "warm" : "off"
                    }`}
                  >
                    <span className="ratio">
                      {consec} / {sf1}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Débit */}
            <div className="card">
              <div className="card-ti">
                Débit
                <span className={`card-meta ${instant.isError ? "err" : ""}`}>
                  {instant.isError ? "indispo" : instant.isFetching ? "…" : "live"}
                </span>
              </div>
              <div className="stats-grid">
                <div className="stat">
                  <span className="k">buys / min</span>
                  <span className={`v ${instant.isError ? "muted" : ""}`}>
                    {instant.isError ? "—" : inst.buys_per_min ?? "—"}
                  </span>
                </div>
                <div className="stat">
                  <span className="k">sells ok · 5m</span>
                  <span className={`v ${instant.isError ? "muted" : ""}`}>
                    {instant.isError ? "—" : inst.sells_ok_5m ?? "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Containers — cAdvisor via Prometheus (projet memebot) */}
          <div className="card">
            <div className="card-ti">
              Containers — {resData.project || "memebot"}
              <span className={`card-meta ${resErr ? "err" : ""}`}>
                {resErr
                  ? "indispo"
                  : resources.isFetching
                  ? "…"
                  : `${cUp}/${cTotal} up`}
              </span>
            </div>
            {resErr ? (
              <div className="note">
                /system/resources indisponible — Prometheus non joignable.
              </div>
            ) : containers.length === 0 ? (
              <div className="note">Aucun container remonté.</div>
            ) : (
              <div className="cgrid">
                {containers.map((c) => (
                  <div className={`cbox ${c.up ? "" : "down"}`} key={c.name}>
                    <span
                      className="cp"
                      style={{
                        background: c.up ? "var(--green)" : "var(--red)",
                        boxShadow: c.up
                          ? "0 0 7px rgba(0,224,138,.6)"
                          : "0 0 7px rgba(255,59,92,.6)",
                      }}
                    />
                    <span className="cn" title={c.name}>{c.name}</span>
                    <span className="cm">
                      {c.up ? `${c.cpu_percent}%` : fmtAge(c.age_s)}
                      <br />
                      {Math.round(c.mem_mb)} Mo
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
